/**
 * Composable pour le Notification Service (microservice).
 * Utiliser receiver_id = slug de l'étudiant (profil ou auth).
 */
import type { Notification, MarkReadResponse, MarkSentResponse } from '@/types/notification'

export function useNotificationService() {
  const config = useRuntimeConfig()
  const baseUrl = config.public.notificationServiceUrl as string

  function normalizeNotificationsPayload(payload: any): Notification[] {
    if (Array.isArray(payload)) return payload
    if (payload && Array.isArray(payload.items)) return payload.items
    return []
  }

  function ensureBaseUrl(): string {
    if (!baseUrl) {
      throw new Error('NUXT_PUBLIC_NOTIFICATION_SERVICE_URL is not configured')
    }
    return baseUrl.replace(/\/$/, '')
  }

  /**
   * Liste les notifications. Tente d'abord avec receiver_id ; si l'API ne l'accepte pas,
   * rappelle sans paramètre. Le store filtre ensuite par receiver_id et channel côté client.
   */
  async function fetchNotifications(receiverId: string): Promise<Notification[]> {
    const url = `${ensureBaseUrl()}/notifications/`
    let data: any
    try {
      data = await $fetch<any>(url, {
        params: { receiver_id: receiverId },
      })
    } catch {
      data = await $fetch<any>(url)
    }
    return normalizeNotificationsPayload(data)
  }

  /**
   * Détail d'une notification par slug.
   */
  async function fetchNotificationDetail(slug: string): Promise<Notification> {
    const url = `${ensureBaseUrl()}/notifications/${slug}`
    return await $fetch<Notification>(url)
  }

  /**
   * Marquer une notification comme lue.
   */
  async function markAsRead(slug: string): Promise<MarkReadResponse> {
    const url = `${ensureBaseUrl()}/notifications/${slug}/mark_read`
    return await $fetch<MarkReadResponse>(url, { method: 'PUT' })
  }

  /**
   * Marquer comme envoyée (optionnel, plutôt backend).
   */
  async function markAsSent(slug: string): Promise<MarkSentResponse> {
    const url = `${ensureBaseUrl()}/notifications/${slug}/mark_sent`
    return await $fetch<MarkSentResponse>(url, { method: 'PUT' })
  }

  /**
   * Supprimer une notification.
   */
  async function deleteNotification(slug: string): Promise<void> {
    const url = `${ensureBaseUrl()}/notifications/${slug}`
    await $fetch(url, { method: 'DELETE' })
  }

  return {
    fetchNotifications,
    fetchNotificationDetail,
    markAsRead,
    markAsSent,
    deleteNotification,
    isConfigured: !!baseUrl,
  }
}
